import React from "react";
import PropTypes from "prop-types";

import EventService from "service/event/EventService";
import useSheetStack from "../common/useSheetStack";
import SheetStack from "../common/SheetStack";
import { LI_V2_EDIT } from "./events";
import LabInvestigationForm from "./LabInvestigationForm";
import DigitiseManuallyCard from "./DigitiseManuallyCard";
import { nameWithTag } from "./data";

/**
 * Lab investigations editor rail — a STACK of write sheets, newest on top.
 *
 * All of the stack behaviour (the blank-sheet guard, one-sheet-per-record,
 * serialised saves, the refusal pulse, the live region, RAIL_OPEN/RAIL_CLOSE)
 * lives in ../common/useSheetStack + ../common/SheetStack, which every v3 rail
 * shares. This file is now only the section's wiring: which events open a
 * sheet, how to pull an investigation out of an edit payload, and what to call
 * each sheet.
 *
 * The rail stays ON DEMAND, exactly as before: SheetStack renders nothing while
 * the stack is empty, and the hook's RAIL_CLOSE hands the column's width back
 * to the table. The form "comes when someone clicks add manually", it does not
 * sit there resting.
 *
 * ONE channel, LI_V2_EDIT, but the payload shape decides what opens:
 *   • {investigation: ...}      — row click on an existing patient row →
 *                                 LabInvestigationForm in Update mode.
 *   • {}  / bare event          — "Add manually" empty state →
 *                                 LabInvestigationForm in Add mode.
 *   • {__digitiseManual: true,  — reviewer clicked "Digitise manually"
 *     docId, documents, ...}      in the Digitise report chooser →
 *                                 DigitiseManuallyCard pre-seeded with the
 *                                 picked document. Card owns the save
 *                                 round-trip (LabInvestigationService.batch).
 *   • {__shortcutReading: true, — standalone-only row click on an existing
 *     investigation: <Inv>}       patient sub-test row → DigitiseManuallyCard
 *                                 in "shortcut" mode: parent lab test +
 *                                 sub-test + LOINC + last unit pre-seeded,
 *                                 fresh test date/value/range, Document ID
 *                                 becomes an in-card dropdown (self-fetched).
 *                                 Save appends a new reading to the SAME
 *                                 Investigation via the batch endpoint's
 *                                 append-only dedup. Not offered in
 *                                 transcript mode.
 */

const EDIT = [LI_V2_EDIT];

const getRecord = (payload) => {
  if (payload && payload.__digitiseManual) {
    // Wrap the digitise payload so useSheetStack's dedup / render
    // branch has something concrete to key off. The __digitiseManual
    // flag survives through `sheet.editing` and the render callback
    // below detects it. `spawnKey` is present when the event came
    // from a card's "+ Add another test" affordance — see
    // `getRecordId` for how it changes the dedup rule.
    return {
      __digitiseManual: true,
      docId:      payload.docId || "",
      documents:  payload.documents || [],
      docToken:   payload.docToken || "",
      docVersion: typeof payload.docVersion === "number" ? payload.docVersion : 0,
      spawnKey:   payload.spawnKey || null,
    };
  }
  if (payload && payload.__shortcutReading && payload.investigation) {
    // Wrap the shortcut payload — a marker + the Investigation snapshot
    // the card seeds from. Save is append-only via the batch endpoint
    // with expectedVersion:-1, so no client-side version snapshot is
    // needed on the payload.
    return {
      __shortcutReading: true,
      investigation:     payload.investigation,
    };
  }
  return payload && payload.investigation;
};

const getRecordId = (r) => {
  if (!r) return null;
  if (r.__digitiseManual) {
    // Two identity rules on the same channel:
    //   • First entry (from the DigitiseModal chooser) has no
    //     spawnKey → dedup by doc id so clicking "Digitise manually"
    //     twice re-focuses the existing sheet instead of stacking a
    //     duplicate.
    //   • Subsequent spawns from a card's "+ Add another test" pass
    //     a fresh timestamp `spawnKey` → each opens as its own sheet
    //     (parallel-edit UX) even though every card shares the same
    //     Document ID.
    if (r.spawnKey) return `digitise-spawn-${r.spawnKey}`;
    return `digitise-${r.docId || "sentinel"}`;
  }
  if (r.__shortcutReading) {
    // One shortcut sheet per Investigation — a repeat row click re-
    // focuses the existing sheet instead of stacking a duplicate.
    const inv = r.investigation || {};
    return `shortcut-${inv.investigationId || (inv.test && inv.test.sltCode) || (inv.test && inv.test.name) || "row"}`;
  }
  return r.investigationId;
};

const testName = (inv) => nameWithTag(inv && inv.test, "That investigation");
const dupMessage = (r) => {
  if (r && r.__digitiseManual) return "The manual capture sheet is already open.";
  if (r && r.__shortcutReading) {
    return `${testName(r.investigation)} is already open — add the new reading in that sheet.`;
  }
  return `${testName(r)} is already open for update.`;
};

const LabInvestigationEditor = ({ patient, appointmentId }) => {
  const patientId = patient && (patient.id || patient.patientID);

  const stack = useSheetStack({
    sectionKey: "lab-investigations",
    editEvents: EDIT,
    getRecord,
    getRecordId,
    blankMessage: "A blank investigation form is already open.",
    dupMessage,
    enabled: !!patientId,
    // LI's "+ Add another test" affordance queues a sibling below the
    // current sheet — mirrors the transcript-mode append-order the
    // reviewer already expects. Other rails (CC / Dx / Med / …) keep
    // the default "newest on top".
    insertAt: "bottom",
  });

  if (!patientId) {
    return <div className="ph-slot-empty is-editor"><p>Waiting for patient…</p></div>;
  }

  return (
    <SheetStack
      stack={stack}
      className="li3-editor"
      describe={(s) => {
        if (s.editing && s.editing.__digitiseManual) return "Digitise manually";
        if (s.editing && s.editing.__shortcutReading) {
          return `Add reading — ${testName(s.editing.investigation)}`;
        }
        return s.editing ? `Update ${testName(s.editing)}` : "Add investigation";
      }}
    >
      {(sheet, props) => {
        if (sheet.editing && sheet.editing.__shortcutReading) {
          const closeSelf = () => props.onClose(props.formId);
          // "+ Add another test" from a shortcut card spawns a sibling
          // via the __digitiseManual channel. The card exposes its
          // currently-picked doc + fetched docs list + viewer token so
          // the sibling inherits them — reviewer doesn't re-pick a
          // document to log a second reading from the same report.
          const spawnSibling = (ctx) => {
            const c = ctx || {};
            EventService.emit(LI_V2_EDIT, {
              __digitiseManual: true,
              docId:      c.docId || "",
              documents:  c.documents || [],
              docToken:   c.docToken || "",
              docVersion: 0,
              spawnKey:   `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
            });
          };
          return (
            <DigitiseManuallyCard
              patientId={Number(patientId)}
              appointmentId={appointmentId}
              shortcutFrom={sheet.editing.investigation}
              /* docVersion is unused for save (batch uses -1) but the
                 card's isStandalone gate checks typeof === "number", so
                 pass a numeric sentinel here. */
              docVersion={0}
              formId={props.formId}
              onClose={closeSelf}
              onSaved={closeSelf}
              onAddAnother={spawnSibling}
              blockedBySibling={props.blockedBySibling}
            />
          );
        }
        if (sheet.editing && sheet.editing.__digitiseManual) {
          const dm = sheet.editing;
          // "+ Add another test" spawns a sibling sheet in the rail
          // that inherits THIS card's Document ID (+ docs list + token
          // + version) so the reviewer doesn't re-pick a doc. Each
          // spawn gets a unique `spawnKey` so getRecordId hands
          // useSheetStack a fresh identity — otherwise the doc-id
          // dedup rule would re-focus the current sheet instead of
          // stacking a new one.
          const spawnSibling = () => {
            EventService.emit(LI_V2_EDIT, {
              __digitiseManual: true,
              docId:      dm.docId,
              documents:  dm.documents,
              docToken:   dm.docToken,
              docVersion: dm.docVersion,
              spawnKey:   `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
            });
          };
          // useSheetStack.closeSheet expects the sheet's id — bind
          // it here so DigitiseManuallyCard can fire onClose /
          // onSaved with a plain no-arg call (which is what the
          // transcript-mode callers also expect).
          const closeSelf = () => props.onClose(props.formId);
          return (
            <DigitiseManuallyCard
              patientId={Number(patientId)}
              appointmentId={appointmentId}
              docId={dm.docId}
              documents={dm.documents}
              docToken={dm.docToken}
              docVersion={dm.docVersion}
              formId={props.formId}
              onClose={closeSelf}
              onSaved={closeSelf}
              onAddAnother={spawnSibling}
              blockedBySibling={props.blockedBySibling}
            />
          );
        }
        return (
          <LabInvestigationForm
            patientId={patientId}
            appointmentId={appointmentId}
            {...props}
          />
        );
      }}
    </SheetStack>
  );
};

LabInvestigationEditor.propTypes = {
  patient: PropTypes.object,
  /** From PatientShell → forwarded onto the form so every Add / Update
   *  stamps the currently-selected appointment on the write body. */
  appointmentId: PropTypes.number,
};

export default LabInvestigationEditor;
